import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaUser, FaHeartbeat, FaEdit, FaStickyNote } from "react-icons/fa";
import Modal from "../Popups/Modal";
import { createFamilyMember, updateFamilyMember } from "../api/family.api";
import {
  parseBp,
  formatBp,
  parseEyeSight,
  formatEyeSight,
  displayBp,
  displayEyeSight,
  validateFamilyMember,
  clampNumericInput,
  clampRemarks,
  REMARKS_MAX,
  BMI_LIMITS,
  EYE_LIMITS,
  NAME_MAX,
} from "../utils/profileValidation";
import { VITAL_LIMITS, preventWheelChange } from "../utils/hraValidation";
import FieldInfo from "./FieldInfo";
import { toast } from "./Toast";

export const EMPTY_MEMBER = {
  fullName: "",
  mobileNumber: "",
  age: "",
  gender: "",
  DOB: "",
  height: "",
  weight: "",
  eyeSight: "",
  BMI: "",
  BP: "",
  bloodGroup: "",
  relation: "",
  description: "",
};

export const RELATIONS = ["Spouse", "Wife", "Husband", "Son", "Daughter", "Father", "Mother", "Child"];
export const GENDERS = ["Male", "Female", "Other"];
export const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const inputClass = "w-full min-h-[44px] border border-surface-container rounded-xl px-3 py-2.5 text-sm bg-surface-lowest focus:outline-none focus:ring-2 focus:ring-primary/40";

export const calcAge = (dob) => {
  if (!dob) return "";
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return String(age);
};

const formatDob = (dob) => {
  if (!dob) return "—";
  const d = new Date(dob.includes("T") ? dob : `${dob}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dob;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const memberToForm = (member) => {
  const bp = parseBp(member?.BP);
  const eye = parseEyeSight(member?.eyeSight);
  const rawPhone = (member?.mobileNumber || member?.contact_number || "").replace(/^\+91/, "").replace(/\D/g, "");
  return {
    fullName: member?.fullName || member?.name || "",
    mobileNumber: member?.mobileNumber || member?.contact_number || "",
    phoneRaw: rawPhone,
    age: member?.age ?? "",
    gender: member?.gender || "",
    DOB: member?.DOB ? member.DOB.split("T")[0] : "",
    height: member?.height ?? "",
    weight: member?.weight ?? "",
    bpSys: bp.sys,
    bpDia: bp.dia,
    eyeLeft: eye.left,
    eyeRight: eye.right,
    BMI: member?.BMI ?? "",
    bloodGroup: member?.bloodGroup || "",
    relation: member?.relation || (member?.isPrimary ? "Primary" : ""),
    description: member?.description || member?.eyeCheckupResults || "",
  };
};

const memberInitials = (name) =>
  (name || "M")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const FieldError = ({ errors, name }) =>
  errors[name] ? <p className="text-xs text-error mt-1">{errors[name]}</p> : null;

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-3">
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon className="text-primary" size={14} />}
      <h3 className="text-sm font-semibold text-on-primary-fixed-var">{title}</h3>
    </div>
    {children}
  </div>
);

const FamilyMemberModal = ({
  open,
  onClose,
  mode: initialMode = "view",
  member = null,
  isPrimary = false,
  staffId,
  onSaved,
}) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(memberToForm(null));
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setForm(memberToForm(member));
      setFieldErrors({});
      setError("");
    }
  }, [open, initialMode, member]);

  const editing = mode === "edit" || mode === "create";
  const displayName = member?.fullName || member?.name || form.fullName || "Member";
  const displayAge = calcAge(member?.DOB?.split("T")[0] || form.DOB) || member?.age || form.age;
  const displayRelation = member?.relation || form.relation || "Member";

  const onChange = (e) => {
    const { name, value } = e.target;
    let next = value;

    if (name === "fullName") next = value.slice(0, NAME_MAX);
    else if (name === "DOB") {
      setForm((f) => ({ ...f, DOB: value, age: calcAge(value) }));
      setFieldErrors((prev) => ({ ...prev, DOB: undefined }));
      return;
    } else if (name === "height") {
      next = clampNumericInput(value, { min: VITAL_LIMITS.heightCm.min, max: VITAL_LIMITS.heightCm.max, decimals: 1 });
    } else if (name === "weight") {
      next = clampNumericInput(value, { min: VITAL_LIMITS.weightKg.min, max: VITAL_LIMITS.weightKg.max, decimals: 1 });
    } else if (name === "BMI") {
      next = clampNumericInput(value, { min: BMI_LIMITS.min, max: BMI_LIMITS.max, decimals: 1 });
    } else if (name === "bpSys") {
      next = clampNumericInput(value, { min: VITAL_LIMITS.bpSys.min, max: VITAL_LIMITS.bpSys.max, decimals: 0 });
    } else if (name === "bpDia") {
      next = clampNumericInput(value, { min: VITAL_LIMITS.bpDia.min, max: VITAL_LIMITS.bpDia.max, decimals: 0 });
    } else if (name === "eyeLeft" || name === "eyeRight") {
      next = clampNumericInput(value, { min: EYE_LIMITS.min, max: EYE_LIMITS.max, decimals: 1 });
    } else if (name === "description") {
      next = clampRemarks(value);
    }

    setForm((f) => ({ ...f, [name]: next }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined, bp: undefined }));
  };

  const handleSave = async () => {
    if (!staffId) return;
    const { valid, errors } = validateFamilyMember(form, "+91");
    setFieldErrors(errors);
    if (!valid) {
      setError("Please fix the highlighted fields.");
      return;
    }

    setSaving(true);
    setError("");
    const payload = {
      ...form,
      age: calcAge(form.DOB) || form.age,
      mobileNumber: form.phoneRaw ? `+91${form.phoneRaw}` : "",
      BP: formatBp(form.bpSys, form.bpDia),
      eyeSight: formatEyeSight(form.eyeLeft, form.eyeRight),
    };
    delete payload.phoneRaw;
    delete payload.bpSys;
    delete payload.bpDia;
    delete payload.eyeLeft;
    delete payload.eyeRight;

    try {
      if (member?._id) {
        const res = await updateFamilyMember(staffId, member._id, payload);
        onSaved?.(res.data.family_member, "update");
        toast.success("Family member updated successfully.");
      } else {
        const res = await createFamilyMember(staffId, payload);
        onSaved?.(res.data.family_member, "create");
        toast.success("Family member added successfully.");
      }
      onClose();
    } catch {
      setError("Failed to save family member.");
      toast.error("Failed to save family member.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditPrimary = () => {
    onClose();
    navigate("/myprofile");
  };

  const viewForm = member ? memberToForm(member) : form;

  return (
    <Modal
      open={open}
      onClose={onClose}
      align="bottom"
      closeSize={24}
      className="max-w-2xl w-full rounded-t-2xl sm:rounded-2xl h-[min(92vh,100%)] sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col !p-0"
    >
      <div className="px-4 sm:px-6 py-4 border-b border-surface-container shrink-0">
        <h2 className="text-lg font-semibold text-on-primary-fixed-var pr-10">
          {mode === "create" ? "Add Family Member" : "Edit Family Member"}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
        {!editing && (
          <div className="flex items-center gap-4 mb-6 p-4 bg-surface-low rounded-xl">
            <div className="w-14 h-14 rounded-full bg-primary-fixed/40 border-2 border-surface-container flex items-center justify-center shrink-0">
              <span className="text-base font-bold text-primary">{memberInitials(displayName)}</span>
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-on-primary-fixed-var break-words">{displayName}</h3>
              <p className="text-sm text-on-surface-variant">
                {displayRelation} • {displayAge ? `${displayAge} Years Old` : "—"}
              </p>
            </div>
          </div>
        )}

        {editing ? (
          <>
            <Section icon={FaUser} title="Basic Info">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-on-surface-variant">Full Name *</label>
                  <input name="fullName" value={form.fullName} onChange={onChange} maxLength={NAME_MAX} className={`${inputClass} mt-1`} />
                  <FieldError errors={fieldErrors} name="fullName" />
                </div>
                <div>
                  <label className="text-xs text-on-surface-variant">Relation *</label>
                  <select name="relation" value={form.relation} onChange={onChange} className={`${inputClass} mt-1`}>
                    <option value="">Select</option>
                    {RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <FieldError errors={fieldErrors} name="relation" />
                </div>
              </div>
            </Section>

            <Section icon={FaUser} title="Contact">
              <div>
                <label className="text-xs text-on-surface-variant">Mobile Number <FieldInfo text="10-digit Indian mobile number. Country code +91 is fixed." /></label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="flex items-center gap-1 px-3 min-h-[44px] border border-surface-container rounded-xl bg-surface-low text-sm text-on-surface-variant whitespace-nowrap select-none">
                    🇮🇳 +91
                  </span>
                  <input
                    type="tel"
                    name="phoneRaw"
                    value={form.phoneRaw || ""}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setForm((f) => ({ ...f, phoneRaw: raw }));
                      setFieldErrors((prev) => ({ ...prev, phoneRaw: undefined }));
                    }}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    className={`${inputClass} flex-1`}
                  />
                </div>
                {fieldErrors.phoneRaw && <p className="text-xs text-error mt-1">{fieldErrors.phoneRaw}</p>}
              </div>
            </Section>

            <Section icon={FaUser} title="Personal">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-on-surface-variant">Date of Birth</label>
                  <input type="date" name="DOB" value={form.DOB} onChange={onChange} className={`${inputClass} mt-1`} />
                  <FieldError errors={fieldErrors} name="DOB" />
                </div>
                <div>
                  <label className="text-xs text-on-surface-variant">Gender</label>
                  <select name="gender" value={form.gender} onChange={onChange} className={`${inputClass} mt-1`}>
                    <option value="">Select</option>
                    {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              </div>
            </Section>

            <Section icon={FaHeartbeat} title="Health Vitals">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-on-surface-variant">Height (cm) <FieldInfo text={`Body height in centimetres. Valid range: ${VITAL_LIMITS.heightCm.min}–${VITAL_LIMITS.heightCm.max} cm.`} /></label>
                  <input type="number" name="height" value={form.height} onChange={onChange} onWheel={preventWheelChange} placeholder={`${VITAL_LIMITS.heightCm.min}–${VITAL_LIMITS.heightCm.max}`} className={`${inputClass} mt-1`} />
                  <FieldError errors={fieldErrors} name="height" />
                </div>
                <div>
                  <label className="text-xs text-on-surface-variant">Weight (kg) <FieldInfo text={`Body weight in kilograms. Valid range: ${VITAL_LIMITS.weightKg.min}–${VITAL_LIMITS.weightKg.max} kg.`} /></label>
                  <input type="number" name="weight" value={form.weight} onChange={onChange} onWheel={preventWheelChange} placeholder={`${VITAL_LIMITS.weightKg.min}–${VITAL_LIMITS.weightKg.max}`} className={`${inputClass} mt-1`} />
                  <FieldError errors={fieldErrors} name="weight" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-on-surface-variant">Blood Pressure (mmHg) <FieldInfo text={`Systolic (upper number): ${VITAL_LIMITS.bpSys.min}–${VITAL_LIMITS.bpSys.max} mmHg. Diastolic (lower number): ${VITAL_LIMITS.bpDia.min}–${VITAL_LIMITS.bpDia.max} mmHg.`} /></label>
                  <div className="flex gap-2 mt-1">
                    <input type="number" name="bpSys" value={form.bpSys} onChange={onChange} onWheel={preventWheelChange} placeholder={`${VITAL_LIMITS.bpSys.min}–${VITAL_LIMITS.bpSys.max}`} className={inputClass} />
                    <span className="self-center text-on-surface-variant">/</span>
                    <input type="number" name="bpDia" value={form.bpDia} onChange={onChange} onWheel={preventWheelChange} placeholder={`${VITAL_LIMITS.bpDia.min}–${VITAL_LIMITS.bpDia.max}`} className={inputClass} />
                  </div>
                  <FieldError errors={fieldErrors} name="bpSys" />
                  <FieldError errors={fieldErrors} name="bpDia" />
                  {fieldErrors.bp && <p className="text-xs text-error mt-1">{fieldErrors.bp}</p>}
                </div>
                <div>
                  <label className="text-xs text-on-surface-variant">BMI <FieldInfo text={`Body Mass Index — weight(kg) ÷ height(m)². Valid range: ${BMI_LIMITS.min}–${BMI_LIMITS.max}.`} /></label>
                  <input type="number" name="BMI" value={form.BMI} onChange={onChange} onWheel={preventWheelChange} placeholder={`${BMI_LIMITS.min}–${BMI_LIMITS.max}`} className={`${inputClass} mt-1`} />
                  <FieldError errors={fieldErrors} name="BMI" />
                </div>
                <div>
                  <label className="text-xs text-on-surface-variant">Blood Group</label>
                  <select name="bloodGroup" value={form.bloodGroup} onChange={onChange} className={`${inputClass} mt-1`}>
                    <option value="">Select</option>
                    {BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-on-surface-variant">Left Eye <FieldInfo text={`Visual acuity — left eye. Enter a value between ${EYE_LIMITS.min}–${EYE_LIMITS.max} (e.g. 6 for 6/6 normal vision).`} /></label>
                  <input type="number" name="eyeLeft" value={form.eyeLeft} onChange={onChange} onWheel={preventWheelChange} placeholder={`${EYE_LIMITS.min}–${EYE_LIMITS.max}`} className={`${inputClass} mt-1`} />
                  <FieldError errors={fieldErrors} name="eyeLeft" />
                </div>
                <div>
                  <label className="text-xs text-on-surface-variant">Right Eye <FieldInfo text={`Visual acuity — right eye. Enter a value between ${EYE_LIMITS.min}–${EYE_LIMITS.max} (e.g. 6 for 6/6 normal vision).`} /></label>
                  <input type="number" name="eyeRight" value={form.eyeRight} onChange={onChange} onWheel={preventWheelChange} placeholder={`${EYE_LIMITS.min}–${EYE_LIMITS.max}`} className={`${inputClass} mt-1`} />
                  <FieldError errors={fieldErrors} name="eyeRight" />
                </div>
              </div>
            </Section>

            <Section icon={FaStickyNote} title="Notes">
              <div className="flex justify-between mb-1">
                <label className="text-xs text-on-surface-variant">Remarks / Notes</label>
                <span className="text-xs text-on-surface-variant">{(form.description || "").length}/{REMARKS_MAX}</span>
              </div>
              <textarea
                name="description"
                value={form.description}
                onChange={onChange}
                rows={3}
                maxLength={REMARKS_MAX}
                placeholder="Add any relevant context..."
                className={`${inputClass} min-h-[88px] resize-y`}
              />
              <FieldError errors={fieldErrors} name="description" />
            </Section>
          </>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs text-on-surface-variant">Mobile</p><p className="font-medium break-words">{viewForm.mobileNumber || "—"}</p></div>
              <div><p className="text-xs text-on-surface-variant">DOB</p><p className="font-medium">{formatDob(viewForm.DOB)}</p></div>
              <div><p className="text-xs text-on-surface-variant">Gender</p><p className="font-medium">{viewForm.gender || "—"}</p></div>
              <div><p className="text-xs text-on-surface-variant">Blood Group</p><p className="font-medium text-primary">{viewForm.bloodGroup || "—"}</p></div>
              <div><p className="text-xs text-on-surface-variant">Height</p><p className="font-medium">{viewForm.height ? `${viewForm.height} cm` : "—"}</p></div>
              <div><p className="text-xs text-on-surface-variant">Weight</p><p className="font-medium">{viewForm.weight ? `${viewForm.weight} kg` : "—"}</p></div>
              <div><p className="text-xs text-on-surface-variant">Blood Pressure</p><p className="font-medium">{displayBp(viewForm.BP || formatBp(viewForm.bpSys, viewForm.bpDia))}</p></div>
              <div><p className="text-xs text-on-surface-variant">BMI</p><p className="font-medium">{viewForm.BMI || "—"}</p></div>
              <div className="sm:col-span-2"><p className="text-xs text-on-surface-variant">Eye Sight</p><p className="font-medium">{displayEyeSight(viewForm.eyeSight || formatEyeSight(viewForm.eyeLeft, viewForm.eyeRight))}</p></div>
            </div>
            {viewForm.description && (
              <div className="p-3 bg-surface-low rounded-xl">
                <p className="text-xs text-on-surface-variant mb-1">Notes</p>
                <p className="text-sm break-words">{viewForm.description}</p>
              </div>
            )}
          </div>
        )}

        {error && <p className="text-sm text-error mt-3">{error}</p>}
      </div>

      <div className="shrink-0 border-t border-surface-container px-4 sm:px-6 py-3 flex gap-3 bg-surface-lowest">
        <button type="button" onClick={onClose} className="flex-1 min-h-[48px] py-2.5 border border-surface-container rounded-xl font-medium text-sm">
          Close
        </button>
        {mode === "view" && isPrimary && (
          <button type="button" onClick={handleEditPrimary} className="flex-1 min-h-[48px] py-2.5 bg-primary text-white rounded-xl font-medium text-sm inline-flex items-center justify-center gap-2">
            <FaEdit size={14} /> Edit Profile
          </button>
        )}
        {mode === "view" && !isPrimary && member && (
          <button type="button" onClick={() => setMode("edit")} className="flex-1 min-h-[48px] py-2.5 bg-primary text-white rounded-xl font-medium text-sm inline-flex items-center justify-center gap-2">
            <FaEdit size={14} /> Edit
          </button>
        )}
        {editing && !isPrimary && (
          <button type="button" onClick={handleSave} disabled={saving} className="flex-1 min-h-[48px] py-2.5 bg-primary text-white rounded-xl font-medium text-sm disabled:opacity-50">
            {saving ? "Saving…" : "Save Profile"}
          </button>
        )}
      </div>
    </Modal>
  );
};

export default FamilyMemberModal;
